import { IconSet } from '@postscript/components';
import useAccountStatus from 'components/account/useAccountStatus';
import { OFFER_SCANNER_ENABLED } from 'components/admin/utils/feature-flags';
import { DiscoveredOffersModal } from 'components/brand/offers/DiscoveredOffersModal';
import {
  isForbiddenError,
  useOfferProposals,
} from 'components/brand/offers/hooks/useOfferProposals';
import {
  ProposalListParams,
  ProposalListResponse,
} from 'components/brand/offers/types';
import { QueryCacheTimes } from 'components/flowBuilder/constants';
import { useBanners } from 'components/GlobalBanners/globalBanners';
import { usePSLabs } from 'controllers/contexts/labsFeatures';
import { useEffect, useRef, useState } from 'react';
import { BANNER_TYPES } from 'utils/events';

// summary.total counts every pending reviewable row whatever the page size.
const PENDING_SUMMARY_PARAMS: ProposalListParams = {
  reviewable: true,
  grouped: true,
  limit: 1,
};

export const OFFER_DISCOVERY_BANNER_PREFIX = 'offer-scanner-discovery-';

// Backends without summary.latestProposalId return only the top-ranked
// group, so its highest id stands in; that misses lower-ranked new rows.
const latestPendingProposalId = (
  data?: ProposalListResponse,
): number | null => {
  const latest = data?.summary.latestProposalId;
  if (typeof latest === 'number') return latest;
  const ids = (data?.promotions ?? []).flatMap((group) =>
    group.proposals.map(({ id }) => id),
  );
  return ids.length > 0 ? Math.max(...ids) : null;
};

const offerCount = (total: number): string =>
  total === 1 ? '1 offer' : `${total} offers`;

/**
 * Adds a notifications-drawer entry while the shop has offers to review and
 * hosts the review modal the entry opens. The entry id carries the newest
 * pending proposal id, so a dismissal holds until a newer proposal arrives.
 */
const OfferDiscoveryNotification = (): JSX.Element => {
  const { addBanner, removeBanner } = useBanners();
  const { hasLabsFlag } = usePSLabs();
  const { billingActive, installed, isFetched } = useAccountStatus();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const shownBannerIdRef = useRef<string | null>(null);
  const openFrameRef = useRef<number | null>(null);
  const enabled =
    hasLabsFlag(OFFER_SCANNER_ENABLED) &&
    isFetched &&
    billingActive &&
    installed;
  // Scans finish in the background and the app never refetches on focus, so
  // an open dashboard re-reads the summary on an interval (foreground only).
  const proposalsQuery = useOfferProposals(PENDING_SUMMARY_PARAMS, {
    enabled,
    refetchInterval: QueryCacheTimes.MEDIUM,
  });
  const available =
    enabled &&
    !(proposalsQuery.isError && isForbiddenError(proposalsQuery.error));
  const total = available ? proposalsQuery.data?.summary.total ?? 0 : 0;
  const latestProposalId = latestPendingProposalId(proposalsQuery.data);
  const bannerId =
    total > 0 && latestProposalId !== null
      ? `${OFFER_DISCOVERY_BANNER_PREFIX}${latestProposalId}`
      : null;

  useEffect(() => {
    if (shownBannerIdRef.current) {
      removeBanner(shownBannerIdRef.current);
      shownBannerIdRef.current = null;
    }
    if (!bannerId) return;
    shownBannerIdRef.current = bannerId;
    addBanner({
      id: bannerId,
      isDismissable: true,
      shouldPersistDismiss: true,
      closesPanelOnPrimaryAction: true,
      customIcon: IconSet.Tag,
      eventTrackingOptions: {
        bannerType: BANNER_TYPES.OFFER_SCANNER_DISCOVERY,
      },
      data: {
        heading: `Shopper found ${offerCount(total)} on your store`,
        bodyText:
          total === 1
            ? 'Review it before turning it into a draft offer. AI can make mistakes, so check it first.'
            : 'Review them to turn the ones you want into draft offers. AI can make mistakes, so check each one.',
        primaryAction: {
          text: total === 1 ? 'Review Offer' : 'Review Offers',
          // The drawer returns focus to its trigger as it closes; opening a
          // frame later lets the modal take focus after that.
          onClick: () => {
            openFrameRef.current = requestAnimationFrame(() => {
              openFrameRef.current = null;
              setIsModalOpen(true);
            });
          },
        },
      },
    });
  }, [bannerId, total]);

  useEffect(
    () => () => {
      if (shownBannerIdRef.current) removeBanner(shownBannerIdRef.current);
      if (openFrameRef.current !== null) {
        cancelAnimationFrame(openFrameRef.current);
      }
    },
    [],
  );

  return (
    <DiscoveredOffersModal
      isOpen={isModalOpen && enabled}
      onClose={() => setIsModalOpen(false)}
    />
  );
};

export default OfferDiscoveryNotification;
